import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { PageHeader } from '@/components/ui/PageHeader'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconCircleCheckmarkOutline } from '@icons/IconCircleCheckmarkOutline'
import { IconCircleDeleteOutline } from '@icons/IconCircleDeleteOutline'
import { IconWarningOutline } from '@icons/IconWarningOutline'
import { ArrowClockwise24Regular } from '@fluentui/react-icons'
import { Suspense, useCallback, useState } from 'react'
import { SectionLayout } from '../shared-components/SectionLayout'
import { ConnectionStatus } from './ConnectionStatus'
import { PlatformBreadcrumbs } from './PlatformBreadcrumbs'
import { RedisMenu } from './RedisMenu'

const statusMap = {
  Check: { Icon: IconCircleCheckmarkOutline, color: 'text-green-500', label: 'Healthy' },
  Warn: { Icon: IconWarningOutline, color: 'text-yellow-500', label: 'Warning' },
  Problem: { Icon: IconCircleDeleteOutline, color: 'text-red-500', label: 'Problem' },
} as const

export function PlatformHealthStatusPage() {
  return (
    <>
      <PageHeader className="pr-3" title={<PlatformBreadcrumbs title="Health Status" />}>
        <RedisMenu />
      </PageHeader>
      <SectionLayout>
        <QueryErrorBoundary errorMessage="Failed to load platform health status">
          <Suspense fallback={<LoadingComponent text="Checking platform health..." />}>
            <PlatformHealthStatusContent />
          </Suspense>
        </QueryErrorBoundary>
      </SectionLayout>
    </>
  )
}

function PlatformHealthStatusContent() {
  const { getSupportApiCheckStatus } = useRestClient()
  const { data: healthStatus, refetch, isFetching } = getSupportApiCheckStatus.useSuspenseQuery(undefined)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await refetch()
    } finally {
      setTimeout(() => setIsRefreshing(false), 300)
    }
  }, [refetch])

  return (
    <div className="flex flex-col gap-6 p-1">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Platform health</h2>
          <p className="text-sm text-muted-foreground">
            Infrastructure, Databricks, and email connectivity checks for the platform.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching || isRefreshing}>
          <ArrowClockwise24Regular className={isFetching || isRefreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      <ConnectionStatus connectionStatus={healthStatus} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {healthStatus.map((status) => {
          const { Icon, color, label } = statusMap[status.icon as keyof typeof statusMap] ?? statusMap.Problem
          return (
            <Card key={status.name}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base font-medium">{status.name}</CardTitle>
                <Icon className={color} size={24} />
              </CardHeader>
              <CardContent>
                <p className={`text-sm font-medium ${color}`}>{label}</p>
                {status.messages ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{status.messages}</p>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No additional details.</p>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
